import { Suspense, lazy, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { WeddingSettings } from '@shared/settings'

/**
 * El sobre se carga solo cuando alguien lo abre. Dentro vive el codificador
 * de QR, que pesa: sin esto, cada invitado que entra a ver la hora de la
 * ceremonia se descarga tambien el generador de codigos QR que quiza nunca
 * use. El sobre ya estaba detras de un boton, asi que no se nota la espera.
 */
const SobreDigital = lazy(() => import('./SobreDigital'))
import {
  BOTON_CLARO,
  BOTON_CONTORNO,
  BotonCopiar,
  EnlaceExterno,
  Seccion,
  cx,
  tieneTexto,
  urlSegura,
} from './comunes'

/* ===========================================================================
   Regalos.

   La sección ofrece tres formas de acompañar a los novios y las tres pesan lo
   mismo: unas palabras, una foto y un sobre. El dinero es un tercio de la
   sección, no la sección — que es justo el reencuadre que evita que una página
   de boda se lea como un peaje.

   Cuando la pareja no prende el sobre digital, la sección se comporta igual
   que siempre: su mensaje, el texto libre de la cuenta y el link de la mesa de
   regalos. Nadie tiene que tocar nada para que su página siga igual.
   =========================================================================== */

type Regalos = WeddingSettings['gifts']

const TITULOS: Record<Regalos['mode'], string> = {
  lluvia_de_sobres: 'Lluvia de sobres',
  mesa_regalos: 'Mesa de regalos',
  ninguno: '',
}

/** El identificador que enlaza el botón «Abrir el sobre» con lo que abre. */
const PANEL_SOBRE = 'sobre-digital'

/**
 * 2 Corintios 9:7. No es un adorno al pie: encabeza la sección y es la razón de
 * que no haya montos, ni contadores, ni recordatorios. Se apaga entero desde el
 * panel, porque esto también tiene que servir a una boda que no es cristiana.
 */
const VERSICULO = {
  titulo: 'Cada uno dé como propuso en su corazón',
  resto: 'no con tristeza ni por obligación, porque Dios ama al que da con alegría.',
  referencia: '2 Corintios 9:7',
}

export function hayRegalos(gifts: Regalos): boolean {
  if (gifts.mode === 'ninguno') return false
  return (
    gifts.envelope?.enabled === true ||
    tieneTexto(gifts.message) ||
    tieneTexto(gifts.accountInfo) ||
    urlSegura(gifts.paymentUrl) !== null
  )
}

export default function Regalos({ gifts }: { gifts: Regalos }) {
  const { slug = '' } = useParams()
  // Sin defaults: si la boda no trae sobre, no hay sobre que pintar. Asi esta
  // seccion no necesita importar nada de @shared/settings en tiempo de
  // ejecucion, y el invitado no se descarga zod para ver la hora de la boda.
  const sobre = gifts.envelope
  const sobreActivo = sobre?.enabled === true
  const [abierto, setAbierto] = useState(false)

  const conVersiculo = sobreActivo && sobre?.showVerse === true
  const pago = urlSegura(gifts.paymentUrl)
  // El texto libre de la cuenta se retira cuando hay sobre: los mismos datos
  // están abajo estructurados y copiables uno a uno, y enseñarlos dos veces
  // solo sirve para que uno de los dos quede desactualizado.
  const cuenta = sobreActivo ? '' : gifts.accountInfo.trim()

  return (
    <Seccion
      id="regalos"
      eyebrow="Regalos"
      titulo={conVersiculo ? VERSICULO.titulo : TITULOS[gifts.mode] || 'Regalos'}
    >
      {conVersiculo && (
        <p className="text-lg leading-relaxed text-tinta/85">
          {VERSICULO.resto}{' '}
          <span className="eyebrow ml-1 text-palo-hondo">{VERSICULO.referencia}</span>
        </p>
      )}

      {tieneTexto(gifts.message) && (
        <p
          className={cx(
            'whitespace-pre-line text-lg leading-relaxed text-tinta/85',
            conVersiculo && 'mt-6',
          )}
        >
          {gifts.message}
        </p>
      )}

      {/* --- Los tres caminos, del mismo tamaño ---------------------------- */}
      <ul className={cx('mt-8 grid gap-4', sobreActivo ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        <Camino
          eyebrow="Unas palabras"
          texto="Firma nuestro libro de mensajes. Lo vamos a leer entero, y más de una vez."
        >
          {/* Ancla de la misma página: el libro es una sección de más abajo.
              Si la pareja apagó esa sección el enlace se queda quieto, y con lo
              que llega hasta aquí no hay forma de saberlo. */}
          <a href="#mensajes" className={BOTON_CONTORNO}>
            Ir al libro
          </a>
        </Camino>

        <Camino
          eyebrow="Una foto"
          texto="Sube las que tomes ese día. Las movidas también: esas suelen ser las buenas."
        >
          <Link to={`/b/${slug}/subir`} className={BOTON_CONTORNO}>
            Subir una foto
          </Link>
        </Camino>

        {sobreActivo && (
          <Camino
            eyebrow="Un sobre"
            texto="La lluvia de sobres de siempre, ahora también desde el teléfono."
          >
            <button
              type="button"
              aria-expanded={abierto}
              aria-controls={PANEL_SOBRE}
              onClick={() => setAbierto((previo) => !previo)}
              className={BOTON_CONTORNO}
            >
              {abierto ? 'Cerrar el sobre' : 'Abrir el sobre'}
            </button>
          </Camino>
        )}
      </ul>

      {sobreActivo && sobre && abierto && (
        <Suspense fallback={<p className="py-6 text-sm text-palo-hondo">Abriendo el sobre…</p>}>
          <SobreDigital id={PANEL_SOBRE} slug={slug} sobre={sobre} />
        </Suspense>
      )}

      {/* --- Lo que ya había guardado la pareja ---------------------------- */}
      {cuenta !== '' && (
        <div className="mt-8 border border-palo/70 bg-lienzo p-5">
          <p className="eyebrow">Datos de la cuenta</p>
          <p className="mt-3 whitespace-pre-line font-mono text-sm leading-relaxed text-tinta">
            {cuenta}
          </p>
          <BotonCopiar texto={cuenta} etiqueta="Copiar los datos" className="mt-5" />
        </div>
      )}

      {pago && (
        <div className="mt-8">
          <EnlaceExterno href={pago} className={BOTON_CLARO}>
            Enviar un regalo en línea
          </EnlaceExterno>
        </div>
      )}
    </Seccion>
  )
}

/** Los tres caminos comparten envoltura para que ninguno se lea como el bueno. */
function Camino({
  eyebrow,
  texto,
  children,
}: {
  eyebrow: string
  texto: string
  children: ReactNode
}) {
  return (
    <li className="flex flex-col items-start border border-palo/70 bg-lienzo p-5">
      <p className="eyebrow">{eyebrow}</p>
      <p className="mt-3 grow text-sm leading-relaxed text-tinta/85">{texto}</p>
      <div className="mt-4">{children}</div>
    </li>
  )
}
